import "./RpcForm.styles.scss";

// Package Imports
import { useMemo, useCallback, createContext, useContext, useState } from "react";
import { JsonForms } from "@jsonforms/react";
import {
  materialRenderers,
  materialCells,
} from "@jsonforms/material-renderers";

// Hook Imports
import { useSchemaProcessor, useUiSchemaGenerator } from "@/hooks";

// Custom Renderers
import { customRenderers } from "./renderers";

// Context to pass validation state to custom renderers
export const ValidationContext = createContext<{ showErrors: boolean }>({
  showErrors: false,
});
export const useValidationContext = () => useContext(ValidationContext);

// Check localStorage for debug mode
const DEBUG =
  typeof window !== "undefined" &&
  localStorage.getItem("DEBUG_JSONFORMS") === "true";

export interface RpcFormProps {
  schema: any;
  formData: any;
  onChange: (data: any) => void;
  onValidationChange?: (errors: any[]) => void;
  className?: string;
  validationMode?: "ValidateAndShow" | "ValidateAndHide" | "NoValidation";
  populateDefaults?: boolean; // Whether to populate default values for required fields
}

/**
 * RpcForm - JSON Forms wrapper for RPC schemas
 *
 * Renders dynamic forms based on JSON Schema with automatic transformations
 * and custom renderers for Pulse Prime RPC schemas.
 *
 * ## Supported Schema Features
 *
 * - **Scalar types**: string, number, integer, boolean
 * - **Complex types**: object, array
 * - **Combinators**:
 *   - `oneOf` - Rendered as dropdown selector
 *   - `anyOf` - Auto-converted to oneOf for objects, flattened for scalars
 *   - `allOf` - Merged into single schema (when no oneOf/anyOf present)
 * - **use_endpoint**: Fetch field values from helper RPCs (e.g., "get_beta")
 * - **additionalProperties**: Transformed to array with __key field for better UX
 * - **enum**: Rendered as autocomplete dropdown
 * - **$ref**: Resolved before rendering
 *
 * ## Schema Transformations
 *
 * The following transformations are applied automatically via useSchemaProcessor:
 *
 * 1. **dereferenceSchema** - Resolves $ref pointers to inline definitions
 * 2. **mergeAllOf** - Flattens allOf combinators (skipped if oneOf/anyOf present)
 * 3. **normalizeScalarAnyOf** - Simplifies type unions:
 *    - `anyOf: [{ type: "string" }, { type: "null" }]` → `type: ["string", "null"]`
 *    - `anyOf: [{ type: "array" }, { type: "null" }]` → `type: ["array", "null"]`
 * 4. **transformAdditionalProperties** - Converts objects with dynamic keys to arrays:
 *    - Schema: `{ type: "object", additionalProperties: { type: "object", properties: {...} } }`
 *    - Becomes: `{ type: "array", items: { type: "object", properties: { __key: {...}, ... } } }`
 *    - Adds `__fromAdditionalProperties` flag for reverse transform on submit
 * 5. **transformObjectAnyOf** - Converts object anyOf to oneOf with discriminators:
 *    - Adds discriminator field (e.g., `signal_parameters_type`)
 *    - Adds `__optionalSection` flag for special handling
 *
 * ## Custom Renderers
 *
 * Registered in `./renderers/index.ts` with rank-based priority (higher rank = higher priority):
 *
 * - **UseEndpointTextControl** (rank 26) - String fields with use_endpoint (fetch button)
 * - **UseEndpointNumberControl** (rank 26) - Number fields with use_endpoint (fetch button)
 * - **TextControl** (rank 25) - Plain string fields (password masking for cr_token)
 * - **NumberControl** (rank 25) - Plain number fields
 * - **AutocompleteControl** (rank 20) - Enum fields (follows allOf/$ref)
 * - **BooleanControl** (rank 10) - Boolean fields (checkboxes)
 * - **EnableableOneOfRenderer** (rank 6) - oneOf with enable/disable switch (__optionalSection)
 * - **SafeAnyOfGuard** (rank 5) - anyOf where all branches are objects
 * - **OneOfDropdownRenderer** (rank 4) - oneOf as dropdown (overrides Material rank 3)
 * - **OptionalFieldsAdder** (rank 2) - Custom layout for optional fields
 *
 * ## Debug Mode
 *
 * Enable debug mode to see schema transformations and renderer selection:
 *
 * ```js
 * localStorage.setItem('DEBUG_JSONFORMS', 'true');
 * // Reload page
 * ```
 *
 * Debug mode shows:
 * - Schema processing pipeline (which transformations ran)
 * - Renderer matching (which renderer handled each field and why)
 * - Form state changes (data and validation errors)
 * - Visual debug panel (collapsible schema inspector)
 *
 * ## Examples
 *
 * ### Basic form
 * ```tsx
 * <RpcForm
 *   schema={rpcSchema}
 *   formData={data}
 *   onChange={handleChange}
 * />
 * ```
 *
 * ### With validation
 * ```tsx
 * <RpcForm
 *   schema={rpcSchema}
 *   formData={data}
 *   onChange={handleChange}
 *   onValidationChange={setErrors}
 *   validationMode="ValidateAndShow"
 * />
 * ```
 *
 * ### With use_endpoint
 * ```tsx
 * // Schema:
 * {
 *   properties: {
 *     beta: {
 *       type: "number",
 *       use_endpoint: "get_beta",  // Shows fetch button
 *       title: "Beta"
 *     }
 *   }
 * }
 *
 * // Wrap with UseEndpointProvider:
 * <UseEndpointProvider interaction={interaction} submitRpc={submitRpc} showError={showError}>
 *   <RpcForm schema={schema} formData={data} onChange={handleChange} />
 * </UseEndpointProvider>
 * ```
 *
 * ### With oneOf dropdown
 * ```tsx
 * // Schema:
 * {
 *   properties: {
 *     strategy_type: {
 *       oneOf: [
 *         { type: "object", title: "Spread", properties: { spread_config: {...} } },
 *         { type: "object", title: "Maker", properties: { maker_config: {...} } }
 *       ]
 *     }
 *   }
 * }
 * // Renders as dropdown selector + form for selected type
 * ```
 *
 * ### With additionalProperties
 * ```tsx
 * // Schema (before transformation):
 * {
 *   properties: {
 *     skew: {
 *       type: "object",
 *       additionalProperties: {
 *         type: "object",
 *         properties: { value: { type: "number" } }
 *       }
 *     }
 *   }
 * }
 * // Transformed to array with __key field:
 * {
 *   properties: {
 *     skew: {
 *       type: "array",
 *       items: {
 *         type: "object",
 *         properties: {
 *           __key: { type: "string", title: "Key" },
 *           value: { type: "number" }
 *         }
 *       },
 *       __fromAdditionalProperties: true  // Flag for reverse transform
 *     }
 *   }
 * }
 * ```
 */
export const RpcForm = ({
  schema,
  formData,
  onChange,
  onValidationChange,
  className = "",
  validationMode = "ValidateAndHide",
  populateDefaults = false,
}: RpcFormProps) => {
  // ALL HOOKS MUST BE CALLED FIRST - before any conditional returns
  const {
    processedSchema,
    isLoading: isProcessingSchema,
    error: schemaError,
  } = useSchemaProcessor(schema);
  const uiSchema = useUiSchemaGenerator(processedSchema);
  const [debugPanelOpen, setDebugPanelOpen] = useState(false);

  // Memoize onChange handler to prevent unnecessary re-renders
  // JSON Forms provides both data and errors in the onChange callback
  const handleChange = useCallback(
    ({ data, errors }: any) => {
      if (DEBUG) {
        console.log(
          "%c🎨 Form State Changed",
          "font-weight: bold; font-size: 13px; color: #e91e63; background: #1a1a1a; padding: 6px 10px; border-radius: 3px;"
        );

        // Show data summary
        const dataKeys = Object.keys(data || {});
        const errorCount = Array.isArray(errors)
          ? errors.length
          : errors
            ? Object.keys(errors).length
            : 0;

        console.log(
          `%c📝 Fields: ${dataKeys.length} | ❌ Errors: ${errorCount}`,
          "color: #03a9f4; font-weight: bold;"
        );

        // Show detailed data in collapsed group
        if (dataKeys.length > 0) {
          console.groupCollapsed("%cData Details", "color: #888; font-style: italic;");
          console.log(data);
          console.groupEnd();
        }

        // Show errors if present
        if (errorCount > 0) {
          console.groupCollapsed(
            "%c❌ Validation Errors",
            "color: #f44336; font-weight: bold;"
          );
          console.log(errors);
          console.groupEnd();
        }
      }

      onChange(data);
      // Pass validation errors to parent for submit prevention
      // JSON Forms errors can be an array or an object with nested errors
      if (onValidationChange) {
        // Flatten errors if they're nested
        const flatErrors = Array.isArray(errors)
          ? errors
          : errors
            ? Object.values(errors)
                .flat()
                .filter((e: any) => e !== null && e !== undefined)
            : [];
        onValidationChange(flatErrors);
      }
    },
    [onChange, onValidationChange],
  );

  // Material renderers first so oneOf/anyOf (rank 3) are preferred; custom renderers override for specific types (higher ranks)
  const allRenderers = useMemo(() => {
    return [...materialRenderers, ...customRenderers];
  }, []); // Empty deps - these are static imports

  // Helper function to recursively transform additionalProperties fields in data
  const transformAdditionalPropsInData = (
    dataObj: any,
    schemaObj: any,
  ): { data: any; changed: boolean } => {
    if (
      !dataObj ||
      typeof dataObj !== "object" ||
      !schemaObj ||
      typeof schemaObj !== "object"
    ) {
      return { data: dataObj, changed: false };
    }

    let result = dataObj;
    let hasChanges = false;

    // Handle oneOf: recursively process each branch's schema (though data matches only one)
    if (Array.isArray(schemaObj.oneOf)) {
      schemaObj.oneOf.forEach((branch: any) => {
        const transformed = transformAdditionalPropsInData(result, branch);
        if (transformed.changed) {
          result = transformed.data;
          hasChanges = true;
        }
      });
    }

    // Handle properties
    if (schemaObj.properties && typeof schemaObj.properties === "object") {
      const newResult = { ...result };

      Object.keys(schemaObj.properties).forEach((key) => {
        const propSchema = schemaObj.properties[key];

        // Transform this property if it's from additionalProperties
        if (
          propSchema?.__fromAdditionalProperties &&
          propSchema?.type === "array"
        ) {
          if (
            newResult[key] &&
            typeof newResult[key] === "object" &&
            !Array.isArray(newResult[key])
          ) {
            const objectData = newResult[key] as Record<string, any>;
            const arrayData = Object.keys(objectData).map((k) => ({
              __key: k,
              ...(typeof objectData[k] === "object" && objectData[k] !== null
                ? objectData[k]
                : {}),
            }));
            newResult[key] = arrayData;
            hasChanges = true;
          }
        }

        // Recursively process nested objects
        if (newResult[key] && typeof newResult[key] === "object") {
          const transformed = transformAdditionalPropsInData(
            newResult[key],
            propSchema,
          );
          if (transformed.changed) {
            newResult[key] = transformed.data;
            hasChanges = true;
          }
        }
      });

      if (hasChanges) {
        result = newResult;
      }
    }

    return { data: result, changed: hasChanges };
  };

  // Memoize form data to prevent creating new object on every render
  // Also ensure nested objects exist so JSON Forms renders all fields
  // When populateDefaults is true (start strategy flow), populate default values for required fields
  const memoizedFormData = useMemo(() => {
    let data = formData || {};

    // First, recursively transform any additionalProperties fields to arrays
    if (processedSchema) {
      const transformed = transformAdditionalPropsInData(data, processedSchema);
      if (transformed.changed) {
        data = transformed.data;
      }
    }

    // Initialize nested objects if they don't exist but are in the schema
    // This ensures JSON Forms renders all fields, even nested ones
    if (processedSchema?.properties) {
      const initialized = { ...data };
      let hasChanges = false;
      const topLevelRequired = processedSchema.required || [];

      Object.keys(processedSchema.properties).forEach((key) => {
        const propSchema = processedSchema.properties[key];
        const isRequired = topLevelRequired.includes(key);

        // If it's an object type and doesn't exist in data, initialize as empty object
        if (propSchema?.type === "object" && !initialized[key]) {
          initialized[key] = propSchema.default
            ? { ...propSchema.default }
            : {};
          hasChanges = true;
        }

        // If it's an array type (including transformed additionalProperties) and doesn't exist, initialize as empty array
        if (propSchema?.type === "array" && !initialized[key]) {
          initialized[key] = propSchema.default
            ? [...(Array.isArray(propSchema.default) ? propSchema.default : [])]
            : [];
          hasChanges = true;
        }

        // Populate default values for required top-level fields (non-objects) when populateDefaults is enabled
        if (
          populateDefaults &&
          isRequired &&
          !(key in initialized) &&
          propSchema?.default !== undefined
        ) {
          initialized[key] = propSchema.default;
          hasChanges = true;
        }

        // Also initialize nested required fields within objects to ensure they render
        if (
          propSchema?.type === "object" &&
          propSchema?.properties &&
          initialized[key]
        ) {
          const nestedObj = { ...initialized[key] }; // Create a copy to avoid mutation
          const nestedRequired = propSchema.required || [];
          let nestedHasChanges = false;

          nestedRequired.forEach((nestedKey: string) => {
            const nestedPropSchema = propSchema.properties[nestedKey];
            // When populateDefaults is enabled, populate default value if defined in schema
            // Otherwise, just initialize as undefined to ensure field renders
            if (!(nestedKey in nestedObj)) {
              nestedObj[nestedKey] =
                populateDefaults && nestedPropSchema?.default !== undefined
                  ? nestedPropSchema.default
                  : undefined;
              nestedHasChanges = true;
            }
          });

          if (nestedHasChanges) {
            initialized[key] = nestedObj;
            hasChanges = true;
          }
        }
      });

      // Only return new object if we made changes, otherwise return original to prevent re-renders
      return hasChanges ? initialized : data;
    }

    return data;
  }, [formData, processedSchema, populateDefaults]);

  // Show loading state while processing schema
  if (isProcessingSchema) {
    return (
      <div className="pulse-typography-body-1 pulse-color--background-100">
        Loading form...
      </div>
    );
  }

  // Show error if schema processing failed
  if (schemaError) {
    return (
      <div className="pulse-color--error-500">
        Error loading form: {schemaError.message}
      </div>
    );
  }

  // Wait for uiSchema to be ready
  if (!processedSchema || !uiSchema || Object.keys(uiSchema).length === 0) {
    return null;
  }

  // Determine if errors should be shown based on validation mode
  const showErrors = validationMode === "ValidateAndShow";

  return (
    <div className={`rpc-form ${className}`}>
      <ValidationContext.Provider value={{ showErrors }}>
        <JsonForms
          schema={processedSchema}
          uischema={uiSchema}
          data={memoizedFormData}
          renderers={allRenderers}
          cells={materialCells}
          onChange={handleChange}
          validationMode={validationMode}
        />
      </ValidationContext.Provider>

      {/* Debug Panel */}
      {DEBUG && (
        <div
          style={{
            marginTop: 16,
            borderTop: "1px solid #444",
            paddingTop: 8,
          }}
        >
          <button
            onClick={() => setDebugPanelOpen(!debugPanelOpen)}
            style={{
              background: "#0e639c",
              color: "white",
              border: "none",
              padding: "8px 16px",
              borderRadius: 4,
              cursor: "pointer",
              fontSize: 12,
              fontFamily: "monospace",
            }}
          >
            {debugPanelOpen ? "▼" : "▶"} Debug: Processed Schema
          </button>
          {debugPanelOpen && (
            <div
              style={{
                marginTop: 8,
                background: "#1e1e1e",
                color: "#d4d4d4",
                padding: 16,
                borderRadius: 4,
                maxHeight: 400,
                overflow: "auto",
                fontSize: 12,
                fontFamily: "monospace",
              }}
            >
              <pre style={{ margin: 0, whiteSpace: "pre-wrap" }}>
                {JSON.stringify(processedSchema, null, 2)}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
